/**
 * renderPage — assemble a complete, Brand-themed HTML page for the client-side
 * publish (Option A). Primitives render as REAL Duro components via
 * renderToStaticMarkup; the page links Duro's prebuilt stylesheet and an inline
 * `:root` Brand override, and the body chrome references Duro's own token vars
 * (so it's themed by the same override). No server, no bundler — the app already
 * has React + Duro compiled in.
 *
 * Handles every widget type: primitives → real Duro components; image/form/grid
 * → deterministic string building (this module is their new home — the workerd
 * renderSite copies go away when materialize moves client-side); text → an
 * injected serializer (the existing 581-line Lexical→HTML, passed by the flow).
 */
import type { ReactElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { DesignSystemModule } from "@website-builder/ds-contract";
// Barrel on purpose — see duroTheme.ts: the deep `tokens/*.css` subpaths ship
// uncompiled StyleX source that throws outside the babel pipeline, and this
// renderer runs in Node (SSR of published sites).
// eslint-disable-next-line duro/no-tokens-barrel-import
import { colors, typography } from "@duro-app/tokens";
import duroCss from "@duro-app/ui/dist/index.css?raw";
import {
  waveformSvg,
  FLAT_PLACEHOLDER,
} from "@website-builder/editor/waveformSvg";
import { WB_RUNTIME_FILE } from "./wbRuntime.js";
import { dsWidgetElement } from "./dsWidgetElement.js";
import { brandToDuroVars, duroVarsToCss } from "./duroTheme.js";
import { DesignSystemScope } from "./designSystemContext.js";
import { duroModule } from "./duroModule.js";
import {
  DEFAULT_PLACEHOLDER_PALETTE,
  placeholderArt,
  type PlaceholderPalette,
  type Tokens,
} from "@website-builder/theme-engine";

export interface PageItem {
  readonly id?: string;
  readonly layout: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  /** Tablet layout override (published in the tablet `@media`). Absent → the
   *  item keeps its desktop layout on tablet. */
  readonly tablet?: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  /** Mobile layout override (published inside a `@media` block). Absent → the
   *  item keeps its (tablet-or-desktop) layout on mobile too. */
  readonly mobile?: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  /** Dropped from the tablet render (`display:none` at tablet width). */
  readonly hiddenTablet?: boolean;
  /** Dropped from the mobile render (`display:none` at mobile width). */
  readonly hiddenMobile?: boolean;
  readonly contentType: string;
  readonly content: Record<string, unknown>;
}

/** A shared header/footer region — a grid rendered into a <header>/<footer>. */
export interface RenderRegion {
  readonly columns: number;
  readonly gapX?: number;
  readonly gapY?: number;
  /** The editor's row height (px); rows are at least this tall. */
  readonly rowHeight?: number;
  readonly items: readonly PageItem[];
}

export interface RenderPageInput {
  readonly title: string;
  readonly columns: number;
  /** Per-axis gutters (px) from the editor's gap editing; absent on old
   *  snapshots → the historical 16px constant. */
  readonly gapX?: number;
  readonly gapY?: number;
  /** The editor's row height (px); rows are at least this tall. Absent on old
   *  snapshots → content-sized rows, as before. */
  readonly rowHeight?: number;
  readonly items: readonly PageItem[];
  readonly tokens: Tokens;
  /** Pre-rendered nav HTML (built once by renderSiteClient from the site's
   *  pages). Rendered inside the shared header/footer wherever a "nav" widget
   *  sits; also the legacy standalone bar when there's no header region. */
  readonly nav?: string;
  /** Shared site header/footer (same on every page). When absent, falls back to
   *  the legacy standalone nav bar (`nav`). */
  readonly header?: RenderRegion;
  readonly footer?: RenderRegion;
  /** Lexical state → HTML for text/editor widgets. Injected (the publish flow
   *  passes the existing serializer) so renderPage stays free of that 581-line
   *  dependency until the shared-render extraction. Absent → text renders empty. */
  /** Site root the page is served under (default "/"). A preview passes its
   *  revision prefix so every link, stylesheet and asset stays inside it. */
  readonly baseUrl?: string;
  /** The design system the widgets render with (default Duro). */
  readonly designSystem?: DesignSystemModule;
  /** An uploaded design system's pre-rendered widgets (see above). */
  readonly customDesignSystem?: CustomDesignSystemRender;
  readonly serializeText?: (editorState: unknown) => string;
}

/** The Duro stylesheet is written once per site under this path. */
export const DURO_CSS_FILE = "duro.css";
/** A site's own design system's stylesheet, published beside its pages. */
export const DS_CSS_FILE = "ds.css";

/**
 * A site rendered with an uploaded design system: its widgets' HTML was
 * produced in the design-system sandbox (and sanitized) — keyed by widget id —
 * and its stylesheet ships as ds.css. The brand reaches that stylesheet as the
 * `--wb-*` variables.
 */
export interface CustomDesignSystemRender {
  readonly html: ReadonlyMap<string, string>;
  /** `:root { --wb-*: … }` for the site's brand. */
  readonly themeVarsCss: string;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;",
  );
}
function escapeAttr(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}

// Page chrome references Duro's own token vars, so the Brand override themes it too.
const PAGE_CSS =
  `*{box-sizing:border-box}` +
  `body{margin:0;background:${colors.bg};color:${colors.text};` +
  `font-family:${typography.fontFamily};line-height:1.5}` +
  `.wb-main{max-width:1100px;margin:0 auto;padding:24px}` +
  `.wb-header{border-bottom:1px solid ${colors.border};padding:12px 24px}` +
  `.wb-footer{border-top:1px solid ${colors.border};padding:24px;margin-top:40px}` +
  `.wb-header .wb-grid,.wb-footer .wb-grid{max-width:1100px;margin:0 auto}` +
  `.wb-nav{border-bottom:1px solid ${colors.border};padding:12px 24px}` +
  `.wb-nav ul{list-style:none;margin:0;padding:0;display:flex;gap:24px}` +
  `.wb-nav a{text-decoration:none;color:${colors.text}}` +
  `.wb-nav a[aria-current="page"]{font-weight:600}` +
  `.wb-grid{display:grid;gap:16px}` +
  `figure.wb-image{margin:0}` +
  `figure.wb-image img{max-width:100%;height:auto;display:block}` +
  `figure.wb-image figcaption{font-size:.85rem;opacity:.7;margin-top:4px}` +
  // Placeholder art fills its cell like the image it stands in for would
  // (a real image keeps its own aspect ratio above).
  `figure.wb-image--placeholder,figure.wb-image--placeholder img{height:100%}` +
  `figure.wb-image--placeholder img{width:100%;object-fit:cover;border-radius:12px}` +
  `.wb-form{display:flex;flex-direction:column;gap:12px;max-width:480px}` +
  `.wb-form label{display:flex;flex-direction:column;gap:4px;font-size:.9rem}` +
  `.wb-form input,.wb-form textarea{font:inherit;padding:8px;` +
  `border:1px solid ${colors.border};border-radius:6px;` +
  `background:${colors.bgCard};color:${colors.text}}` +
  // Audio waveform player. No-JS fallback: the static waveform + native
  // <audio controls> show; the custom toggle/time stay hidden. wb-runtime.js
  // adds `.wb-audio--ready`, which flips to the custom player and hides the
  // native controls. The played layer is revealed by the --wb-pct var.
  `figure.wb-audio{margin:0;padding:10px 14px;border:1px solid ${colors.border};` +
  `border-radius:10px;background:${colors.bgCard}}` +
  `.wb-audio__row{display:flex;align-items:center;gap:12px}` +
  `.wb-audio__toggle{display:none;flex:none;width:34px;height:34px;border:none;` +
  `border-radius:999px;background:${colors.accent};color:${colors.accentContrast};` +
  `cursor:pointer;font-size:.9rem;line-height:1;align-items:center;justify-content:center}` +
  `.wb-audio--ready .wb-audio__toggle{display:inline-flex}` +
  `.wb-audio__wave{position:relative;flex:1;height:48px;min-width:0}` +
  `.wb-audio--ready .wb-audio__wave{cursor:pointer}` +
  `.wb-audio__base,.wb-audio__played{position:absolute;inset:0}` +
  `.wb-audio__base{color:${colors.border}}` +
  `.wb-audio__played{color:${colors.accent};` +
  `clip-path:inset(0 calc(100% - var(--wb-pct,0%)) 0 0)}` +
  `.wb-audio__base svg,.wb-audio__played svg{display:block;width:100%;height:100%}` +
  `.wb-audio__time{display:none;flex:none;font-size:.72rem;color:${colors.textMuted};` +
  `font-variant-numeric:tabular-nums;min-width:74px;text-align:right}` +
  `.wb-audio--ready .wb-audio__time{display:inline-block}` +
  `.wb-audio__native{display:block;width:100%;margin-top:8px}` +
  `.wb-audio--ready .wb-audio__native{display:none}`;

interface Ctx {
  readonly serializeText?: (editorState: unknown) => string;
  /** Where the site's root is served ("/" published; a preview revision's
   *  prefix in the editor). Always ends with "/". */
  readonly base?: string;
  readonly ds?: DesignSystemModule;
  readonly custom?: CustomDesignSystemRender;
  /** Brand colours the placeholder art for asset-less image slots is drawn
   *  from — the same two the Brand override themes the page with. */
  readonly palette?: PlaceholderPalette;
  /** Pre-built nav HTML (page links, per-page aria-current) — rendered wherever
   *  a "nav" widget appears (i.e. inside the shared header/footer). */
  readonly nav?: string;
  /** Accumulators (shared, mutable) for per-cell responsive CSS rules gathered
   *  during render. renderPage wraps each in its own `@media` block, tablet
   *  before mobile so the mobile block wins where both match. */
  readonly tabletRules?: string[];
  readonly mobileRules?: string[];
  /** Numbers the grids that need a tier rule (`data-g`), in render order. */
  readonly gridSeq: { next: number };
}

/** Escape a string for use inside a double-quoted CSS attribute selector
 *  (`[data-c="…"]`). Item ids are `id_*`/slug-safe, but escape defensively. */
function cssAttrEscape(s: string): string {
  return s.replace(/["\\]/g, (c) => "\\" + c);
}

// Published `@media` breakpoints. Tablet is the wider net (≤1024 → 641–1024 is
// tablet-only; ≤640 matches both, and the mobile block — emitted AFTER tablet —
// wins there). They mirror the editor's 768/390 device frames.
const TABLET_MAX_WIDTH = 1024;
const MOBILE_MAX_WIDTH = 640;

/** grid-column/grid-row declarations for a rect, `!important` to beat the inline
 *  desktop base. */
function gridDecl(r: { x: number; y: number; w: number; h: number }): string {
  return (
    `grid-column:${r.x + 1} / span ${r.w}!important;` +
    `grid-row:${r.y + 1} / span ${r.h}!important`
  );
}
const samePos = (
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

function str(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

/** A site-root path ("_assets/x", "duro.css") under the page's base. */
function siteUrl(ctx: { readonly base?: string }, path: string): string {
  return `${ctx.base ?? "/"}${path}`;
}

export function renderImage(
  c: Record<string, unknown>,
  ctx: { readonly palette?: PlaceholderPalette; readonly base?: string } = {},
): string {
  // Crop-by-pan framing (Phase 4): object-position chooses which part of the
  // object-fit:cover image the frame shows. Inline so it travels with the tag.
  const pos = str(c["objectPosition"]);
  const posStyle =
    pos && pos !== "50% 50%"
      ? ` style="object-position:${escapeAttr(pos)}"`
      : "";
  // An asset-less slot draws on-brand placeholder art (inline data: URI —
  // no asset to fetch) instead of a broken `/_assets/` reference. Decorative:
  // it carries no alt text of its own; the author's alt still applies once a
  // real image is set. The srcs are attribute-escaped like any other value.
  const asset = str(c["asset"]);
  const src = asset
    ? siteUrl(ctx, `_assets/${asset}`)
    : placeholderArt(
        ctx.palette ?? DEFAULT_PLACEHOLDER_PALETTE,
        str(c["seed"]) ?? "image",
      );
  const alt = asset ? (str(c["alt"]) ?? "") : "";
  const img =
    `<img src="${escapeAttr(src)}"` +
    `${posStyle} alt="${escapeAttr(alt)}" loading="lazy"/>`;
  const caption = str(c["caption"]);
  const cap = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : "";
  const cls = asset ? "wb-image" : "wb-image wb-image--placeholder";
  return `<figure class="${cls}">${img}${cap}</figure>`;
}

export function renderVideo(
  c: Record<string, unknown>,
  ctx: { readonly base?: string } = {},
): string {
  const asset = escapeAttr(str(c["asset"]) ?? "");
  const mime = escapeAttr(str(c["mime"]) ?? "video/mp4");
  // controls default on; autoplay implies muted (browsers block unmuted
  // autoplay). Same content-addressed bytes as images, served at /_assets/<sha>.
  const attrs = [
    c["controls"] === false ? "" : "controls",
    c["autoplay"] ? "autoplay" : "",
    c["loop"] ? "loop" : "",
    c["muted"] || c["autoplay"] ? "muted" : "",
    "playsinline",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    `<video class="wb-video" ${attrs} preload="metadata" ` +
    `style="width:100%;height:100%;object-fit:cover">` +
    `<source src="${siteUrl(ctx, `_assets/${asset}`)}" type="${mime}"></video>`
  );
}

export function renderAudio(
  c: Record<string, unknown>,
  ctx: { readonly base?: string } = {},
): string {
  const asset = escapeAttr(str(c["asset"]) ?? "");
  const mime = escapeAttr(str(c["mime"]) ?? "audio/mpeg");
  // The waveform is baked from the precomputed peaks (nothing untrusted is
  // injected — they're ints from serialize). Absent → a flat placeholder.
  const raw = Array.isArray(c["peaks"]) ? (c["peaks"] as unknown[]) : [];
  const peaks = raw.filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n),
  );
  const svg = waveformSvg(peaks.length ? peaks : [...FLAT_PLACEHOLDER]);
  const duration =
    typeof c["duration"] === "number" && Number.isFinite(c["duration"])
      ? c["duration"]
      : 0;
  return (
    `<figure class="wb-audio" data-wb-audio data-duration="${duration}">` +
    `<div class="wb-audio__row">` +
    `<button class="wb-audio__toggle" type="button" aria-label="Play">▶</button>` +
    `<div class="wb-audio__wave" role="slider" tabindex="0" aria-label="Seek" ` +
    `aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">` +
    `<div class="wb-audio__base">${svg}</div>` +
    `<div class="wb-audio__played">${svg}</div>` +
    `</div>` +
    `<span class="wb-audio__time">0:00</span>` +
    `</div>` +
    `<audio class="wb-audio__native" controls preload="metadata">` +
    `<source src="${siteUrl(ctx, `_assets/${asset}`)}" type="${mime}"></audio>` +
    `</figure>`
  );
}

function renderField(f: Record<string, unknown>): string {
  const type = str(f["type"]) ?? "text";
  const name = escapeAttr(str(f["name"]) ?? "");
  const label = escapeHtml(str(f["label"]) ?? "");
  const req = f["required"] ? " required" : "";
  if (type === "textarea") {
    const rows = typeof f["rows"] === "number" ? ` rows="${f["rows"]}"` : "";
    return `<label>${label}<textarea name="${name}"${rows}${req}></textarea></label>`;
  }
  if (type === "checkbox") {
    return `<label><input type="checkbox" name="${name}"${req}/>${label}</label>`;
  }
  const t = type === "email" ? "email" : "text";
  return `<label>${label}<input type="${t}" name="${name}"${req}/></label>`;
}

function renderForm(id: string, c: Record<string, unknown>): string {
  const honeypot =
    `<input type="text" name="website" value="" tabindex="-1"` +
    ` autocomplete="off" aria-hidden="true"` +
    ` style="position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden"/>`;
  const fields = Array.isArray(c["fields"]) ? c["fields"] : [];
  const body = fields
    .map((f) => renderField(f as Record<string, unknown>))
    .join("");
  const submit = `<button type="submit">${escapeHtml(str(c["submitButtonLabel"]) ?? "Send")}</button>`;
  return (
    `<form class="wb-form" method="post" action="/_forms/${escapeAttr(id)}">` +
    `${honeypot}${body}${submit}</form>`
  );
}

/**
 * A section that is a form: its grid goes inside a native <form>, so Enter in
 * a field and a click on a submit button both submit — no JavaScript. The
 * form's box is `display:contents`, so the grid lays out exactly as before.
 * Only actions the serializer validated reach here; anything else renders
 * the grid alone.
 */
function wrapInForm(form: unknown, grid: string): string {
  const f = form as
    | {
        id?: unknown;
        action?: { kind?: unknown; url?: unknown; method?: unknown };
      }
    | undefined;
  const a = f?.action;
  if (
    !f ||
    typeof f.id !== "string" ||
    a?.kind !== "navigate" ||
    a.method !== "get" ||
    typeof a.url !== "string" ||
    !a.url.startsWith("https://")
  )
    return grid;
  return (
    `<form id="${escapeAttr(f.id)}" method="get" action="${escapeAttr(a.url)}"` +
    ` style="display:contents">${grid}</form>`
  );
}

/** A design-system widget, rendered with the page's design system — or, for
 *  an uploaded one, the HTML the sandbox rendered for it. */
function renderDs(ctx: Ctx, item: PageItem, element: ReactElement): string {
  if (ctx.custom) return ctx.custom.html.get(item.id ?? "") ?? "";
  return renderToStaticMarkup(
    <DesignSystemScope ds={ctx.ds ?? duroModule}>{element}</DesignSystemScope>,
  );
}

function renderWidget(item: PageItem, ctx: Ctx): string {
  const { contentType, content } = item;
  switch (contentType) {
    case "button":
    case "input":
    case "heading":
    case "card":
    case "icon":
      return renderDs(ctx, item, dsWidgetElement(contentType, content)!);
    case "text":
    case "editor":
      return ctx.serializeText ? ctx.serializeText(content["editorState"]) : "";
    case "image":
      return renderImage(content, ctx);
    case "video":
      return renderVideo(content, ctx);
    case "audio":
      return renderAudio(content, ctx);
    case "form":
      return renderForm(item.id ?? "", content);
    case "nav":
      // The page-links menu (built by renderSiteClient from the site's pages).
      return ctx.nav ?? "";
    case "spacer":
      // Pure layout whitespace: render nothing — renderCell still positions the
      // cell (grid-column/grid-row span), so the empty area reserves the space.
      return "";
    // An Inline is a nested grid in its own cell: its children are placed by
    // their layouts, so the row's order and justify are already in the cells.
    case "inline-grid":
    case "grid": {
      const columns =
        typeof content["columns"] === "number" ? content["columns"] : 12;
      const items = Array.isArray(content["items"])
        ? (content["items"] as PageItem[])
        : [];
      const grid = renderGrid(
        columns,
        items,
        ctx,
        {
          x: typeof content["gapX"] === "number" ? content["gapX"] : undefined,
          y: typeof content["gapY"] === "number" ? content["gapY"] : undefined,
        },
        typeof content["rowHeight"] === "number"
          ? content["rowHeight"]
          : undefined,
      );
      return wrapInForm(content["form"], grid);
    }
    default:
      return "";
  }
}

/** A widget's vertical placement in its cell (`valign`), as the cell's
 *  `align-content`: the cell stays a block, so the responsive rules that
 *  hide and show it keep working. Absent or `top` adds nothing. Rows are at
 *  least the editor's height (renderGrid), so a cell spanning three rows has
 *  room to place it in. */
function cellPlacement(content: Record<string, unknown>): string {
  return content["valign"] === "middle"
    ? ";align-content:center"
    : content["valign"] === "bottom"
      ? ";align-content:end"
      : "";
}

function renderCell(item: PageItem, ctx: Ctx): string {
  const base = item.layout;
  const style = `grid-column:${base.x + 1} / span ${base.w};grid-row:${base.y + 1} / span ${base.h}${cellPlacement(item.content)}`;
  // Responsive layers need a stable selector to target inside @media. The base
  // layout is INLINE, so the rules use !important to win. The tablet block also
  // matches mobile widths (≤1024 ⊇ ≤640), so the mobile block only emits what
  // DIFFERS from the effective tablet state — a true cascade base→tablet→mobile.
  const id = item.id;
  const responsive =
    item.tablet || item.mobile || item.hiddenTablet || item.hiddenMobile;
  if (!id || !ctx.tabletRules || !ctx.mobileRules || !responsive) {
    return `<div class="wb-cell" style="${style}">${renderWidget(item, ctx)}</div>`;
  }
  const sel = `[data-c="${cssAttrEscape(id)}"]`;

  // Tablet block (≤1024).
  const tablet: string[] = [];
  if (item.hiddenTablet) tablet.push("display:none!important");
  else if (item.tablet) tablet.push(gridDecl(item.tablet));
  if (tablet.length) ctx.tabletRules.push(`${sel}{${tablet.join(";")}}`);

  // Mobile block (≤640) — only the delta from what tablet already applies.
  const effTabPos = item.tablet ?? base; // position in force at mobile width
  const effTabHidden = !!item.hiddenTablet;
  const mobPos = item.mobile ?? base;
  const mobHidden = !!item.hiddenMobile;
  const mobile: string[] = [];
  if (mobHidden) {
    if (!effTabHidden) mobile.push("display:none!important");
  } else {
    if (effTabHidden) mobile.push("display:block!important"); // un-hide vs tablet
    if (!samePos(mobPos, effTabPos)) mobile.push(gridDecl(mobPos));
  }
  if (mobile.length) ctx.mobileRules.push(`${sel}{${mobile.join(";")}}`);

  const dataAttr = ` data-c="${escapeAttr(id)}"`;
  return `<div class="wb-cell"${dataAttr} style="${style}">${renderWidget(item, ctx)}</div>`;
}

function renderGrid(
  columns: number,
  items: readonly PageItem[],
  ctx: Ctx,
  gap?: { x?: number; y?: number },
  rowHeight?: number,
): string {
  // CSS `gap` is row-gap then column-gap; .wb-grid's 16px stays the
  // fallback for snapshots published before gap editing existed.
  const gapStyle =
    gap && (gap.x !== undefined || gap.y !== undefined)
      ? `;gap:${gap.y ?? 16}px ${gap.x ?? 16}px`
      : "";
  const rows = rowHeight ? `grid-auto-rows:minmax(${rowHeight}px,auto)` : "";
  // A cell hidden at a tier leaves its rows in the grid; at the minimum height
  // they would be blank bands. So in that tier the grid goes back to
  // content-sized rows, as before (and a "middle" cell there is centred only
  // beside a taller neighbour). The tablet block also matches mobile widths,
  // and the mobile block comes after it, so mobile restores the minimum when
  // only tablet hid. `!important`: the base style is inline, as for cells.
  const hidesTablet = items.some((i) => i.hiddenTablet);
  const hidesMobile = items.some((i) => i.hiddenMobile);
  let gridAttr = "";
  if (
    rows &&
    (hidesTablet || hidesMobile) &&
    ctx.tabletRules &&
    ctx.mobileRules
  ) {
    const g = `g${ctx.gridSeq.next++}`;
    gridAttr = ` data-g="${g}"`;
    const sel = `[data-g="${g}"]`;
    if (hidesTablet)
      ctx.tabletRules.push(`${sel}{grid-auto-rows:auto!important}`);
    if (hidesMobile)
      ctx.mobileRules.push(`${sel}{grid-auto-rows:auto!important}`);
    else ctx.mobileRules.push(`${sel}{${rows}!important}`);
  }
  return (
    `<div class="wb-grid"${gridAttr} style="grid-template-columns:repeat(${columns},1fr)${gapStyle}${rows ? `;${rows}` : ""}">` +
    items.map((i) => renderCell(i, ctx)).join("") +
    `</div>`
  );
}

function renderRegion(
  tag: "header" | "footer",
  region: RenderRegion,
  ctx: Ctx,
): string {
  const inner = renderGrid(
    region.columns,
    region.items,
    ctx,
    { x: region.gapX, y: region.gapY },
    region.rowHeight,
  );
  return `<${tag} class="wb-${tag}">${inner}</${tag}>`;
}

export function renderPage(input: RenderPageInput): string {
  const brandOverride = duroVarsToCss(brandToDuroVars(input.tokens));
  // Cells push their responsive rules here as the page/header/footer render;
  // emitted as @media blocks below (after everything has rendered into them).
  const tabletRules: string[] = [];
  const mobileRules: string[] = [];
  const ctx: Ctx = {
    serializeText: input.serializeText,
    ...(input.baseUrl ? { base: input.baseUrl } : {}),
    ...(input.designSystem ? { ds: input.designSystem } : {}),
    ...(input.customDesignSystem ? { custom: input.customDesignSystem } : {}),
    palette: {
      primary: input.tokens.color.primarySolid,
      accent: input.tokens.color.accent,
    },
    nav: input.nav,
    tabletRules,
    mobileRules,
    gridSeq: { next: 0 },
  };
  const grid = renderGrid(
    input.columns,
    input.items,
    ctx,
    { x: input.gapX, y: input.gapY },
    input.rowHeight,
  );
  // Shared header/footer render the same on every page; the nav widget inside
  // renders this page's nav (per-page aria-current). Legacy fallback: no header
  // region → the standalone nav bar.
  const header = input.header
    ? renderRegion("header", input.header, ctx)
    : (input.nav ?? "");
  const footer = input.footer ? renderRegion("footer", input.footer, ctx) : "";
  // Responsive layers gathered from every cell (page grid, header, footer, nested
  // grids). Tablet BEFORE mobile so the mobile block wins where both @media match.
  const tabletCss = tabletRules.length
    ? `@media (max-width:${TABLET_MAX_WIDTH}px){${tabletRules.join("")}}`
    : "";
  const mobileCss = mobileRules.length
    ? `@media (max-width:${MOBILE_MAX_WIDTH}px){${mobileRules.join("")}}`
    : "";
  // Ship the hydration script only when this page actually has a progressively-
  // enhanced widget (today: audio), so audio-free pages stay 100% no-JS. The
  // file itself is always published by renderSiteClient.
  const needsRuntime = (header + grid + footer).includes("data-wb-audio");
  const runtime = needsRuntime
    ? `<script src="${siteUrl(ctx, WB_RUNTIME_FILE)}" defer></script>`
    : "";
  return (
    `<!doctype html><html lang="en"><head>` +
    `<meta charset="utf-8"/>` +
    `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
    `<title>${escapeHtml(input.title)}</title>` +
    `<link rel="stylesheet" href="${siteUrl(ctx, ctx.custom ? DS_CSS_FILE : DURO_CSS_FILE)}"/>` +
    `<style>${ctx.custom ? ctx.custom.themeVarsCss : ""}${brandOverride}${PAGE_CSS}${tabletCss}${mobileCss}</style>` +
    runtime +
    `</head><body>${header}<main class="wb-main">${grid}</main>${footer}</body></html>`
  );
}

/** Duro's prebuilt stylesheet, to write alongside the rendered pages as /duro.css. */
export function duroStylesheet(): string {
  return duroCss;
}
